import { comparer } from 'mobx';

import {
  AnalyticsStore,
  codeSplit,
  ContextStore,
  Controller,
  ExperimentStore,
  FetchOptions,
  merge,
  PageStore,
  SerializedData
} from 'bernie-core';
import { LocationReactorResult } from 'bernie-client';
import { PageData } from 'bernie-http';

import {
  getMultiItemMutationImpactingKeys,
  getSearchImpactingKeys,
  SearchCriteriaModel,
  singleUseQueryParams,
  untrackedQueryParams
} from 'stores/models';
import { CustomerInterestsStore } from 'stores/customer-interests-store';
import { MultiItemStore } from 'stores/multi-item-store';
import { SearchCriteriaStore } from 'stores/search-criteria-store';
import { SearchQueryAdapter } from 'stores/adapters/search-query-adapter';
import { SearchResultsStore } from 'stores/search-results-store';

import { CustomerInterest } from 'models/common/customer-interest';
import { PageName } from 'constants/pages';

import { meta } from 'utils/meta';

import { convertHashParams, shouldHashOverrideDefaultValues, shouldSearchHashParams } from './converters/legacy-hash-params';
import { processClientSideDefaultRequestFields } from './search-controller-modules/early-return';

const { withoutKeys, withKeys } = merge;

export class SearchController implements Controller {
  public bundles = ['search'];
  public component = codeSplit(() => import(/* webpackChunkName: "search" */ 'views/hotel/search.jsx'));
  public pageId = `${PageName.SEARCH},H,20`;
  public path = '/Hotel-Search*';
  public navigationId = 'hotel';
  public exact = true;
  public routeName = 'search';
  public routeData = {
    rfrrNamespace: 'HOT.SR',
    pageName: 'page.Hotel-Search'
  };

  public async fetch(options: FetchOptions): Promise<any> {
    if (!options || !options.stores) {
      return Promise.reject('SearchController.fetch receieved an invalid StoreContainer');
    }

    const { isServer, query, request, stores } = options;
    const context: ContextStore = stores.get<ContextStore>('context');
    const page: PageStore = stores.get<PageStore>('page');
    const analyticsStore: AnalyticsStore = stores.get<AnalyticsStore>('analytics');
    const searchCriteria: SearchCriteriaStore = stores.get<SearchCriteriaStore>('searchCriteria');
    const searchResults: SearchResultsStore = stores.get<SearchResultsStore>('searchResults');
    const experiments: ExperimentStore = stores.get<ExperimentStore>('experiment');
    const customerInterestsStore: CustomerInterestsStore = stores.get<CustomerInterestsStore>('customerInterestsStore');
    const multiItem: MultiItemStore = stores.get<MultiItemStore>('multiItem');

    // sanitize incoming params
    const singleUseCriteria: any = withKeys(singleUseQueryParams).merge({}, options.query);
    const untrackedKeys = [...untrackedQueryParams, ...singleUseCriteria, 'prevQuery'];
    const prevQuery = withoutKeys(untrackedKeys).merge({}, query.prevQuery);
    const nextQuery = withoutKeys(untrackedKeys).merge({}, query);

    this.initializeMultiItem(multiItem, query, nextQuery, context);

    page.navigationId = this.navigationId;
    page.pageId = this.pageId;
    searchResults.pageData = page;

    if (searchResults.foregoClientFetch) {
      searchResults.foregoClientFetch = false;
      const criteria = searchResults.searchCriteria.toJSON();
      const sqaParsedOut = SearchQueryAdapter.parseOut(criteria);

      return { query: sqaParsedOut, replace: true } as LocationReactorResult;
    }
    // if client side detects hash params, handle them
    if (!isServer && shouldSearchHashParams(nextQuery)) {
      const hashParams = convertHashParams();

      if (shouldHashOverrideDefaultValues(hashParams)) {
        searchResults.defaultRequestFields = {};
      }
      if (hashParams) {
        return { query: hashParams, replace: true } as LocationReactorResult;
      }
    }
    // server rendered with defaulted params, we need to adjust URLs first
    let newQuery = processClientSideDefaultRequestFields(isServer, searchResults, nextQuery);
    if (newQuery) {
      return newQuery;
    }

    const forceFetch = searchResults.shouldFetch;
    // check if queries are different to previous state
    if (forceFetch || !comparer.structural(nextQuery, prevQuery)) {
      // queries are meaningfully diff, setup base stores again
      searchCriteria.setValues({
        ...SearchCriteriaStore.getDefaults(),
        ...SearchCriteriaStore.getFilterDefaults(),
        ...nextQuery
      });

      const modelData = SearchQueryAdapter.parseIn(searchCriteria.toJSON());
      const incomingSearchCriteria = new SearchCriteriaModel(modelData);
      let customerInterests: CustomerInterest[] = customerInterestsStore.customerInterests;

      if (this.shouldFetchCustomerInterests(experiments, isServer)) {
        experiments.logExperiment(context, page, experiments.exposures.PWA_Affinity_from_customer_spinner);
        customerInterests = await customerInterestsStore.fetch(context, incomingSearchCriteria.semdtl);
        if (customerInterests.length > 0) {
          searchResults.foregoClientFetch = true;
        }
      }
      // fetches are only carried out if criteria is also changed
      if (forceFetch || this.hasStoresChanged(searchResults.searchCriteria, incomingSearchCriteria)) {
        const skipSearchResultsFetch = isServer === true && query && query.skipServerFetch === 'true';
        await this.doNetworking(
          page,
          analyticsStore,
          context,
          searchResults,
          incomingSearchCriteria,
          skipSearchResultsFetch,
          singleUseCriteria,
          searchCriteria,
          experiments,
          customerInterests,
          multiItem,
          request && request.headers
        );
      }

      if (!isServer && searchResults.alternateDateFetchUrl) {
        searchResults.fetchAlternateDates(context, searchResults.alternateDateFetchUrl);
      }
    }
    // cleanup defaultRequestFields so they don't hang around for subsequent calls on clientside
    newQuery = processClientSideDefaultRequestFields(isServer, searchResults, nextQuery);
    if (newQuery) {
      return newQuery;
    }
  }

  private shouldFetchCustomerInterests(experiments: ExperimentStore, isServer: boolean): boolean {
    const { PWA_Affinity_from_customer_spinner } = experiments.exposures;

    return isServer && PWA_Affinity_from_customer_spinner && PWA_Affinity_from_customer_spinner.bucket === 1;
  }

  private initializeMultiItem(multiItem: MultiItemStore, query: SerializedData, nextQuery: SerializedData, context: ContextStore) {
    multiItem.buildContext(query);
    multiItem.referrer = query.rfrr;

    if (multiItem.isPackageShoppingPath()) {
      this.navigationId = 'package';
      this.pageId = `${PageName[`SEARCH_PACKAGE_${multiItem.multiItemContext.packageType}`]},P,20`;
    }

    if (query['multiProp'] === 'true' && !query['businessModelFilter'] && nextQuery) {
      const defaultBusinessModel = multiItem.getDefaultBusinessModel();
      if (multiItem.cartInfo && multiItem.cartInfo.identifier === 'mixAndMatch') {
        multiItem.removeMixAndMatchCart();
        multiItem.getOrCreateCart(context, defaultBusinessModel);
      }
      nextQuery['businessModelFilter'] = defaultBusinessModel;
    }
  }

  private async doNetworking(
    page: PageStore,
    analyticsStore: AnalyticsStore,
    context: ContextStore,
    searchResults: SearchResultsStore,
    incomingSearchCriteria: SearchCriteriaModel,
    skipServerFetch: boolean,
    singleUseCriteria: SerializedData,
    searchCriteria: SearchCriteriaStore,
    experiments: ExperimentStore,
    customerInterests: CustomerInterest[],
    multiItem: MultiItemStore,
    headers?: SerializedData
  ) {
    const pageFetch = page.fetch(meta['hotel-search'], context.site, context.locale);

    if (
      multiItem &&
      multiItem.isPackageShoppingPath() &&
      !multiItem.isServerSide() &&
      this.shouldCallMultiItemSessionMutation(searchResults.searchCriteria, incomingSearchCriteria)
    ) {
      await searchResults.createSessionBeforeMultiItemFetch(multiItem, context, incomingSearchCriteria);
    }

    // Add an exception to allow for the filters to be updated if overfiltered
    if (!searchResults.isError || searchResults.overFiltered) {
      searchResults.updateSearchCriteria(incomingSearchCriteria);
      const searchResultsFetch = skipServerFetch
        ? Promise.resolve()
        : searchResults.fetch(analyticsStore, context, customerInterests, experiments, singleUseCriteria, multiItem, headers);

      await pageFetch;
      await searchResultsFetch;

      this.updateSearchCriteriaFromSearchResults(searchResults, searchCriteria);
    }
  }

  public fetchPageData(): Promise<PageData> {
    // Temporary fix for issue: https://jira.expedia.biz/browse/BHS-17025
    const noIndexSeo = { seo: { robots: 'noindex,nofollow' } };
    const navId = { navigationId: this.navigationId };

    return Promise.resolve({
      ...noIndexSeo,
      ...navId
    }) as Promise<PageData>;
  }

  private updateSearchCriteriaFromSearchResults(searchResults, searchCriteria) {
    // BFF may provide regionId when coming from SEM or alternate dates
    if (searchResults.searchCriteria) {
      searchCriteria.startDate = searchResults.searchCriteria.startDate;
      searchCriteria.endDate = searchResults.searchCriteria.endDate;
      searchCriteria.regionId = searchResults.searchCriteria.regionId;
    }
  }

  private getComparableKeys(prevStore: SearchCriteriaModel, newStore: any) {
    let compareKeys = getSearchImpactingKeys();
    if (!this.didGeoChange(prevStore, newStore)) {
      compareKeys = compareKeys.filter(key => key !== 'regionId' && key !== 'latLong' && key !== 'destination');
    }

    // If we're asking to sort by values that's equivalent to what we currently have,
    // remove sort field from the comparison to avoid another service call
    const askingForEmptySortWhileWeHaveRecommended = newStore.sort === '' && prevStore.sort === 'recommended';
    const askingForMctc2SortWhileWeHaveDeals = !newStore.sort && newStore.mctc === '2' && prevStore.sort === 'deals';
    const equivalentSort = askingForEmptySortWhileWeHaveRecommended || askingForMctc2SortWhileWeHaveDeals;
    if (equivalentSort) {
      compareKeys = compareKeys.filter(key => key !== 'sort');
    }

    return compareKeys;
  }

  private didGeoChange(prevStore: SearchCriteriaModel, newStore: any) {
    const { regionId: prevRegionId, latLong: prevLatLong, destination: prevDestination } = prevStore;
    const { regionId: newRegionId, latLong: newLatLong, destination: newDestination } = newStore;

    const isNewRegionIdEmpty = newRegionId === '';
    const isNewLatLongEmpty = newLatLong === '';
    const isNewDestinationEmpty = newDestination === '';
    const didRegionIdChange = !isNewRegionIdEmpty && newRegionId !== prevRegionId;
    const didLatLongChange = !isNewLatLongEmpty && newLatLong !== prevLatLong;
    const didDestinationChange = !isNewDestinationEmpty && newDestination !== prevDestination;

    if (didRegionIdChange || (isNewRegionIdEmpty && didLatLongChange)) {
      return true;
    }

    return isNewRegionIdEmpty && isNewLatLongEmpty && didDestinationChange;
  }

  private shouldCallMultiItemSessionMutation(prevSearchCriteria: SearchCriteriaModel, newSearchCriteria: SearchCriteriaModel) {
    if (!Boolean(prevSearchCriteria) || !Boolean(newSearchCriteria)) {
      return false;
    }

    return this.hasStoreWithKeysChanged(prevSearchCriteria, newSearchCriteria, getMultiItemMutationImpactingKeys());
  }

  private hasStoresChanged(prevStore: SearchCriteriaModel, newStore: SearchCriteriaModel) {
    if (Boolean(newStore) && !Boolean(prevStore)) {
      return true;
    }

    return this.hasStoreWithKeysChanged(prevStore, newStore, this.getComparableKeys(prevStore, newStore));
  }

  private hasStoreWithKeysChanged(prevSearchCriteria: SearchCriteriaModel, newSearchCriteria: SearchCriteriaModel, compareKeys: string[]) {
    const prevSearchParams = withKeys(compareKeys).merge({}, prevSearchCriteria.toJSON());
    const newSearchParams = withKeys(compareKeys).merge({}, newSearchCriteria.toJSON());

    return !comparer.structural(prevSearchParams, newSearchParams);
  }
}
